<template>
  <v-navigation-drawer
    :value="value"
    right
    temporary
    fixed
    width="380"
    @input="$emit('input', $event)">
    <!-- Header -->
    <div class="d-flex align-center px-4 py-3">
      <v-icon class="mr-2">
        mdi-filter-variant
      </v-icon>
      <span class="subtitle-1 font-weight-bold">ตัวกรองการจอง</span>
      <v-spacer />
      <v-btn
        icon
        @click="$emit('input', false)">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>
    <v-divider />

    <!-- Filters -->
    <div class="pa-4">
      <!-- Keyword search -->
      <div class="caption grey--text mb-1">
        ค้นหา
      </div>
      <v-text-field
        :value="filters.search"
        outlined
        dense
        hide-details
        placeholder="Booking No"
        prepend-inner-icon="mdi-magnify"
        clearable
        class="mb-4"
        @input="onFilterChange('search', $event)"
        @click:clear="onFilterChange('search', '')" />

      <!-- Vendor -->
      <div class="caption grey--text mb-1">
        Vendor
      </div>
      <v-autocomplete
        :value="filters.vendors"
        :items="vendorItems"
        item-text="text"
        item-value="value"
        multiple
        outlined
        dense
        hide-details
        :loading="loadingVendors"
        placeholder="เลือกโรงงาน..."
        class="mb-4 chip-gap"
        :menu-props="{ maxWidth: 350 }"
        @input="onFilterChange('vendors', $event)">
        <template v-slot:selection="{ item, selected, select, parent }">
          <v-chip
            small
            close
            :input-value="selected"
            @click="select"
            @click:close="parent.selectItem(item)">
            <span class="chip-label-truncate">{{ item.text }}</span>
          </v-chip>
        </template>
      </v-autocomplete>

      <!-- Collection -->
      <div class="caption grey--text mb-1">
        Collection
      </div>
      <v-autocomplete
        :value="filters.collections"
        :items="collectionItems"
        item-text="text"
        item-value="value"
        multiple
        outlined
        dense
        hide-details
        :loading="loadingCollections"
        placeholder="เลือก Collection"
        class="mb-4 chip-gap"
        :menu-props="{ maxWidth: 380 }"
        @input="onFilterChange('collections', $event)">
        <template v-slot:selection="{ item, selected, select, parent }">
          <v-chip
            small
            close
            :input-value="selected"
            @click="select"
            @click:close="parent.selectItem(item)">
            <span class="chip-label-truncate">{{ item.text }}</span>
          </v-chip>
        </template>
      </v-autocomplete>

      <!-- Item No -->
      <div class="caption grey--text mb-1">
        SKU Code
      </div>
      <v-text-field
        :value="filters.itemNo"
        outlined
        dense
        hide-details
        placeholder="ระบุ sku code"
        clearable
        class="mb-5"
        @input="onFilterChange('itemNo', $event)"
        @click:clear="onFilterChange('itemNo', '')" />

      <!-- Clear all -->
      <v-btn
        v-if="activeCount > 0"
        text
        x-small
        color="grey darken-1"
        class="mb-4 px-1"
        @click="$emit('clear')">
        <v-icon
          x-small
          class="mr-1">
          mdi-refresh
        </v-icon>
        ล้างตัวกรอง
      </v-btn>
    </div>

    <v-divider v-if="activeCount > 0" />

    <!-- Results -->
    <template v-if="activeCount > 0">
      <v-progress-linear
        v-if="loadingResults"
        indeterminate
        color="secondary" />

      <template v-else>
        <div class="d-flex align-center px-4 py-2">
          <span class="caption grey--text">พบ <strong>{{ resultTotal }}</strong> รายการ</span>
        </div>
        <v-divider />

        <div class="result-list">
          <div
            v-if="searchResults.length === 0"
            class="caption grey--text text-center py-6">
            ไม่พบรายการ
          </div>

          <template v-else>
            <div
              v-for="(booking, index) in searchResults"
              :key="booking.id">
              <v-divider
                v-if="index > 0"
                :key="`div-${booking.id}`" />
              <div
                class="result-item px-4 py-3">
                <!-- Row 1: bookingNo + status -->
                <div class="d-flex align-center justify-space-between mb-1">
                  <span
                    class="subtitle-2 font-weight-bold primary--text text-decoration-underline"
                    style="cursor:pointer"
                    @click="openBooking(booking)">
                    #{{ booking.bookingNo }}
                    <v-icon
                      x-small
                      color="primary">
                      mdi-open-in-new
                    </v-icon>
                  </span>
                  <v-chip
                    small
                    label
                    :color="statusColor(booking.status)"
                    :text-color="statusTextColor(booking.status)">
                    {{ statusLabel(booking.status) }}
                  </v-chip>
                </div>

                <!-- Row 2: vendor -->
                <div
                  class="body-2 font-weight-medium text-truncate mb-1"
                  style="max-width:220px">
                  {{ vendorName(booking) }}
                </div>

                <!-- Row 3: date + time -->
                <div class="d-flex align-center body-2 grey--text mb-2">
                  <v-icon
                    small
                    class="mr-1 grey--text">
                    mdi-calendar-outline
                  </v-icon>
                  <span class="mr-3">{{ booking.date ? $dayjs(booking.date).format('DD/MM/YYYY') : '-' }}</span>
                  <v-icon
                    small
                    class="mr-1 grey--text">
                    mdi-clock-outline
                  </v-icon>
                  {{ slotTime(booking) }}
                </div>

                <!-- Row 4: collections -->
                <div
                  v-if="booking.collections && booking.collections.length"
                  class="d-flex flex-wrap mb-1">
                  <v-chip
                    v-for="col in booking.collections"
                    :key="col"
                    small
                    outlined
                    color="primary"
                    class="mr-1 mb-1 no-hover">
                    {{ col }}
                  </v-chip>
                </div>

                <!-- Row 5: item nos -->
                <template v-if="itemNos(booking).length">
                  <div class="d-flex flex-wrap">
                    <v-chip
                      v-for="no in visibleItemNos(booking)"
                      :key="no"
                      small
                      color="grey lighten-3"
                      class="mr-1 mb-1 no-hover"
                      style="font-family: monospace;">
                      {{ no }}
                    </v-chip>
                    <v-chip
                      v-if="!expandedBookings[booking.id] && itemNos(booking).length > ITEM_NO_LIMIT"
                      small
                      color="secondary lighten-4"
                      class="mr-1 mb-1"
                      style="cursor:pointer"
                      @click.stop="toggleExpand(booking.id)">
                      แสดงทั้งหมด
                    </v-chip>
                    <v-chip
                      v-if="expandedBookings[booking.id] && itemNos(booking).length > ITEM_NO_LIMIT"
                      small
                      color="secondary lighten-4"
                      class="mr-1 mb-1"
                      style="cursor:pointer"
                      @click.stop="toggleExpand(booking.id)">
                      แสดงน้อยลง
                    </v-chip>
                  </div>
                </template>
              </div>
            </div>
          </template>
        </div>

        <template v-if="pageCount > 1">
          <v-divider />
          <div class="d-flex justify-center py-2">
            <v-pagination
              v-model="resultPage"
              :length="pageCount"
              :total-visible="5"
              color="secondary"
              circle />
          </div>
        </template>
      </template>
    </template>
  </v-navigation-drawer>
</template>

<script>
import ProductCollectionProvider from '@/resources/ProductCollectionProvider'
import VendorsProvider from '@/resources/VendorsProvider'
import DeliveryBookingProvider from '@/resources/DeliveryBookingProvider'
import {
  statusLabel,
  statusBg,
  statusTextColor
} from '@/assets/js/BookingStatusUtils'

const productCollectionService = new ProductCollectionProvider()
const vendorsService = new VendorsProvider()
const deliveryBookingService = new DeliveryBookingProvider()

export default {
  name: 'DeliveryBookingListFilter',

  props: {
    value: {
      type: Boolean,
      default: false
    },
    filters: {
      type: Object,
      default: () => ({ search: '', vendors: [], collections: [], itemNo: '' })
    }
  },

  data () {
    return {
      vendorItems: [],
      loadingVendors: false,
      collectionItems: [],
      loadingCollections: false,

      searchResults: [],
      resultTotal: 0,
      loadingResults: false,
      resultPage: 1,
      resultPageSize: 30,

      expandedBookings: {},
      ITEM_NO_LIMIT: 3
    }
  },

  computed: {
    activeCount () {
      return (this.filters.search?.trim() ? 1 : 0)
        + this.filters.vendors.length
        + this.filters.collections.length
        + (this.filters.itemNo?.trim() ? 1 : 0)
    },

    pageCount () {
      return Math.ceil(this.resultTotal / this.resultPageSize) || 1
    }
  },

  watch: {
    filters: {
      deep: true,
      handler () {
        this.resultPage = 1
        this.debouncedSearch()
      }
    },

    resultPage () {
      this.fetchResults()
    }
  },

  async mounted () {
    this.debouncedSearch = this.$debounce(this.fetchResults, 400)
    await Promise.all([this.fetchVendors(), this.fetchCollections()])
  },

  methods: {
    async fetchVendors () {
      this.loadingVendors = true
      try {
        const { data } = await vendorsService.getItems({ status: 'active', limit: 9999 })
        this.vendorItems = (data.results || []).map((v) => ({
          text: v.name,
          value: v.id
        }))
      } catch (e) {
        this.vendorItems = []
      } finally {
        this.loadingVendors = false
      }
    },

    async fetchCollections () {
      this.loadingCollections = true
      try {
        const { data } = await productCollectionService.getAllStore({ isAllStore: true })
        this.collectionItems = (data || []).map((c) => ({
          text: c.name.toUpperCase(),
          value: c.nickname
        }))
      } catch (e) {
        this.collectionItems = []
      } finally {
        this.loadingCollections = false
      }
    },

    async fetchResults () {
      if (this.activeCount === 0) {
        this.searchResults = []
        this.resultTotal = 0
        return
      }

      this.loadingResults = true
      try {
        const query = {
          sortBy: 'date',
          sortOrder: 'desc',
          page: this.resultPage,
          limit: this.resultPageSize
        }

        if (this.filters.search?.trim()) {
          query.search = this.filters.search.trim()
        }
        if (this.filters.vendors.length) {
          query.vendorIds = this.filters.vendors.join(',')
        }
        if (this.filters.collections.length) {
          query.collections = this.filters.collections.join(',')
        }
        if (this.filters.itemNo?.trim()) {
          query.skuCodes = this.filters.itemNo.trim()
        }

        const { data } = await deliveryBookingService.getWarehouseBookings(query)
        this.searchResults = data.results || []
        this.resultTotal = data.totalDocs || 0
        this.expandedBookings = {}
      } catch (e) {
        this.searchResults = []
        this.resultTotal = 0
      } finally {
        this.loadingResults = false
      }
    },

    onFilterChange (field, value) {
      this.$emit('update:filters', { ...this.filters, [field]: value ?? '' })
    },

    vendorName (booking) {
      return booking.vendors?.map((v) => v.vendorName).join(', ') || '-'
    },

    slotTime (booking) {
      if (!booking.slots?.length) return '-'
      return `${booking.slots[0].startTime} - ${booking.slots[0].endTime}`
    },

    itemNos (booking) {
      const pos = booking.purchaseOrders || []
      const nos = pos.flatMap((po) => (po.lines || []).map((l) => l.itemNo).filter(Boolean))
      return [...new Set(nos)]
    },

    visibleItemNos (booking) {
      const all = this.itemNos(booking)
      return this.expandedBookings[booking.id] ? all : all.slice(0, this.ITEM_NO_LIMIT)
    },

    toggleExpand (id) {
      this.$set(this.expandedBookings, id, !this.expandedBookings[id])
    },

    openBooking (booking) {
      const { href } = this.$router.resolve({
        name: 'DeliveryBookingDetail',
        params: { id: booking.id }
      })
      window.open(href, '_blank')
    },

    statusLabel: (s) => statusLabel(s),
    statusColor: (s) => statusBg(s),
    statusTextColor: (s) => statusTextColor(s)
  }
}
</script>

<style scoped>
::v-deep .chip-gap .v-chip {
  margin: 2px 4px 2px 0;
}

::v-deep .no-hover::before {
  display: none !important;
}

.chip-label-truncate {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  vertical-align: middle;
}
</style>
